import {
  Grid,
  Box,
  TextField,
  Button,
  Switch,
  MenuItem,
  Typography,
  IconButton,
} from "@material-ui/core";
import { useState, useEffect } from "react";
import Axios from "axios";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";

import { useAdminStyles } from "../../components/admin/adminStyles";
import { AdminPageHeader } from "../../components/admin/AdminPageHeader";
import { useToast } from "../../components/shared/Toast";
import { Loading } from "../../components/shared/loading";
import { PRODUCT_ICONS } from "../../data/productIcons";

import AddIcon from "@material-ui/icons/Add";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import ArrowForwardIcon from "@material-ui/icons/ArrowForward";

const headKey = "qweqeasdj123n1jasndjn2j3njsdn12i891bdjsdu1283djsd1231231";
const ICON_NAMES = Object.keys(PRODUCT_ICONS).sort();
const STATUS_OPTIONS = [
  { value: "live", label: "Live (flagship)" },
  { value: "saas", label: "SaaS product" },
  { value: "soon", label: "Coming soon" },
];
const GROUP_OPTIONS = [
  { value: "product", label: "Products" },
  { value: "saas", label: "SaaS" },
  { value: "game", label: "Games" },
];
// content keys handled by the visual fields below; the rest goes to the JSON box.
const HANDLED = [
  "slug", "name", "expansion", "category", "status", "group", "statusLabel", "icon",
  "logo", "accent", "heroBadges", "tagline", "listSummary", "overview",
  "highlights", "featuresTitle", "featuresSubtitle", "features", "categories",
  "awards", "platforms", "quote", "cta",
];

const s = (v) => (v === undefined || v === null ? "" : v);

// Small icon-name <select>
const IconField = ({ label, value, onChange }) => {
  const classes = useAdminStyles();
  return (
    <TextField
      select
      label={label}
      variant="outlined"
      size="small"
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      fullWidth
      className={classes.textField}
      InputLabelProps={{ shrink: true }}
    >
      <MenuItem value="">(none)</MenuItem>
      {ICON_NAMES.map((n) => (
        <MenuItem key={n} value={n}>
          {n}
        </MenuItem>
      ))}
    </TextField>
  );
};

export const ProductForm = ({ mode, id }) => {
  const classes = useAdminStyles();
  const navigate = useNavigate();
  const toast = useToast();
  const [loading, setLoading] = useState(mode === "edit");
  const [submitting, setSubmitting] = useState(false);

  // flat columns
  const [slug, setSlug] = useState("");
  const [name, setName] = useState("");
  const [status, setStatus] = useState("saas");
  const [group, setGroup] = useState("saas");
  const [category, setCategory] = useState("");
  const [priority, setPriority] = useState("1");
  const [visible, setVisible] = useState(true);
  const [metaTitle, setMetaTitle] = useState("");
  const [metaDesc, setMetaDesc] = useState("");
  const [metaKeywords, setMetaKeywords] = useState("");
  const [ogImage, setOgImage] = useState("");
  // content scalars
  const [expansion, setExpansion] = useState("");
  const [icon, setIcon] = useState("Widgets");
  const [logo, setLogo] = useState("");
  const [statusLabel, setStatusLabel] = useState("");
  const [tagline, setTagline] = useState("");
  const [listSummary, setListSummary] = useState("");
  const [overview, setOverview] = useState("");
  const [featuresTitle, setFeaturesTitle] = useState("");
  const [featuresSubtitle, setFeaturesSubtitle] = useState("");
  const [quote, setQuote] = useState("");
  const [accentFrom, setAccentFrom] = useState("#6b1b6b");
  const [accentTo, setAccentTo] = useState("#4c004c");
  const [accentSoft, setAccentSoft] = useState("#c77dc7");
  // repeaters
  const [heroBadges, setHeroBadges] = useState([]);
  const [highlights, setHighlights] = useState([]);
  const [features, setFeatures] = useState([]);
  const [platforms, setPlatforms] = useState([]);
  const [catTitle, setCatTitle] = useState("");
  const [catSubtitle, setCatSubtitle] = useState("");
  const [catItems, setCatItems] = useState([]);
  const [awTitle, setAwTitle] = useState("");
  const [awSubtitle, setAwSubtitle] = useState("");
  const [awItems, setAwItems] = useState([]);
  // cta
  const [ctaLabel, setCtaLabel] = useState("");
  const [ctaNote, setCtaNote] = useState("");
  const [ctaTo, setCtaTo] = useState("/get-in-touch");
  const [ctaHref, setCtaHref] = useState("");
  // advanced (saasModel, replaces, toolkit, howItWorks + any extra keys)
  const [advanced, setAdvanced] = useState("{}");

  useEffect(() => {
    if (mode !== "edit") return;
    const fd = new FormData();
    fd.append("id", id);
    Axios.post("/api/admin/getproduct", fd, { headers: { headKey } }).then(
      (res) => {
        const row = res.data && res.data.data;
        if (!row) {
          toast.error("Product not found.");
          navigate("/dash/allproducts");
          return;
        }
        setSlug(s(row.slug));
        setName(s(row.name));
        setStatus(s(row.status) || "saas");
        setCategory(s(row.category));
        setPriority(String(row.priority || 1));
        setVisible(row.visible === 1);
        setMetaTitle(s(row.meta_title));
        setMetaDesc(s(row.meta_desc));
        setMetaKeywords(s(row.meta_keywords));
        setOgImage(s(row.og_image));
        let c = {};
        try {
          c = JSON.parse(row.content || "{}");
        } catch (e) {
          c = {};
        }
        setExpansion(s(c.expansion));
        setGroup(s(c.group) || "saas");
        setIcon(s(c.icon) || "Widgets");
        setLogo(s(c.logo));
        setStatusLabel(s(c.statusLabel));
        setTagline(s(c.tagline));
        setListSummary(s(c.listSummary));
        setOverview(s(c.overview));
        setFeaturesTitle(s(c.featuresTitle));
        setFeaturesSubtitle(s(c.featuresSubtitle));
        setQuote(s(c.quote));
        if (c.accent) {
          setAccentFrom(s(c.accent.from) || "#6b1b6b");
          setAccentTo(s(c.accent.to) || "#4c004c");
          setAccentSoft(s(c.accent.soft) || "#c77dc7");
        }
        setHeroBadges(Array.isArray(c.heroBadges) ? c.heroBadges : []);
        setHighlights(Array.isArray(c.highlights) ? c.highlights : []);
        setFeatures(Array.isArray(c.features) ? c.features : []);
        setPlatforms(Array.isArray(c.platforms) ? c.platforms : []);
        if (c.categories) {
          setCatTitle(s(c.categories.title));
          setCatSubtitle(s(c.categories.subtitle));
          setCatItems(c.categories.items || []);
        }
        if (c.awards) {
          setAwTitle(s(c.awards.title));
          setAwSubtitle(s(c.awards.subtitle));
          setAwItems(c.awards.items || []);
        }
        if (c.cta) {
          setCtaLabel(s(c.cta.label));
          setCtaNote(s(c.cta.note));
          setCtaTo(s(c.cta.to));
          setCtaHref(s(c.cta.href));
        }
        const rest = {};
        Object.keys(c).forEach((k) => {
          if (!HANDLED.includes(k)) rest[k] = c[k];
        });
        setAdvanced(JSON.stringify(rest, null, 2));
        setLoading(false);
      }
    );
  }, [id, mode]);

  const buildContent = () => {
    let extra = {};
    try {
      extra = JSON.parse(advanced || "{}");
    } catch (e) {
      throw new Error("Advanced sections is not valid JSON.");
    }
    const content = {
      slug: slug.trim(),
      name: name.trim(),
      expansion,
      category,
      icon,
      accent: { from: accentFrom, to: accentTo, soft: accentSoft },
      status,
      group,
      statusLabel,
      heroBadges: heroBadges.filter((b) => b && b.trim()),
      tagline,
      listSummary,
      overview,
      highlights,
      featuresTitle,
      featuresSubtitle,
      features,
      quote,
      cta: {
        label: ctaLabel,
        note: ctaNote,
        ...(ctaTo ? { to: ctaTo } : {}),
        ...(ctaHref ? { href: ctaHref } : {}),
      },
      ...extra,
    };
    if (logo) content.logo = logo;
    if (platforms.length) content.platforms = platforms;
    if (catItems.length)
      content.categories = { title: catTitle, subtitle: catSubtitle, items: catItems };
    if (awItems.length)
      content.awards = { title: awTitle, subtitle: awSubtitle, items: awItems };
    return content;
  };

  const onSubmit = () => {
    if (!slug.trim()) return toast.error("Slug is required.");
    if (!name.trim()) return toast.error("Name is required.");
    let content;
    try {
      content = buildContent();
    } catch (e) {
      return toast.error(e.message);
    }
    setSubmitting(true);
    const fd = new FormData();
    if (mode === "edit") fd.append("id", id);
    fd.append("slug", slug.trim());
    fd.append("name", name.trim());
    fd.append("status", status);
    fd.append("category", category);
    fd.append("priority", priority);
    fd.append("meta_title", metaTitle);
    fd.append("meta_desc", metaDesc);
    fd.append("meta_keywords", metaKeywords);
    fd.append("og_image", ogImage);
    fd.append("visible", visible ? "1" : "0");
    fd.append("content", JSON.stringify(content));
    const url = mode === "edit" ? "/api/admin/update_product" : "/api/admin/add_product";
    Axios.post(url, fd, { headers: { headKey } })
      .then((res) => {
        setSubmitting(false);
        if (res.data.status) {
          toast.success(res.data.msg || "Saved");
          navigate("/dash/allproducts");
        } else {
          toast.error(res.data.msg || "Something went wrong.");
        }
      })
      .catch(() => {
        setSubmitting(false);
        toast.error("Network error. Please try again.");
      });
  };

  // ── repeater helpers ──
  const upd = (list, setList, i, key, val) => {
    const next = list.map((row, idx) =>
      idx === i ? { ...row, [key]: val } : row
    );
    setList(next);
  };
  const add = (setList, list, row) => setList([...list, row]);
  const del = (setList, list, i) => setList(list.filter((_, idx) => idx !== i));

  const RepeaterHead = ({ title, onAdd }) => (
    <Box
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginTop: 8,
        marginBottom: 8,
      }}
    >
      <Typography className={classes.sectionTitle} style={{ margin: 0 }}>
        {title}
      </Typography>
      <Button
        size="small"
        className={classes.secondaryBtn}
        startIcon={<AddIcon />}
        onClick={onAdd}
      >
        Add
      </Button>
    </Box>
  );

  if (loading) return <Loading />;

  return (
    <div>
      <Helmet>
        <title>
          {mode === "edit" ? "Edit" : "Add"} Product - Admin | Arawinz
        </title>
      </Helmet>
      <AdminPageHeader
        eyebrow="Products"
        title={mode === "edit" ? "Edit product" : "Add product"}
        subtitle="Edit everything shown on the product card and detail page."
        backTo="/dash/allproducts"
      />

      <Grid container spacing={3}>
        {/* Basics */}
        <Grid item xs={12}>
          <Typography className={classes.sectionTitle}>Basics</Typography>
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField label="Slug (URL)" variant="outlined" size="small" value={slug}
            onChange={(e) => setSlug(e.target.value)} required fullWidth
            className={classes.textField} InputLabelProps={{ shrink: true }}
            placeholder="e.g. arawinz-vault" />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField label="Name" variant="outlined" size="small" value={name}
            onChange={(e) => setName(e.target.value)} required fullWidth
            className={classes.textField} InputLabelProps={{ shrink: true }} />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField label="Category" variant="outlined" size="small" value={category}
            onChange={(e) => setCategory(e.target.value)} fullWidth
            className={classes.textField} InputLabelProps={{ shrink: true }} />
        </Grid>
        <Grid item xs={12} sm={4} md={3}>
          <TextField select label="Category tab" variant="outlined" size="small" value={group}
            onChange={(e) => setGroup(e.target.value)} fullWidth
            className={classes.textField} InputLabelProps={{ shrink: true }}
            helperText="Filter tab on the products page">
            {GROUP_OPTIONS.map((o) => (
              <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12} sm={4} md={3}>
          <TextField select label="Availability" variant="outlined" size="small" value={status}
            onChange={(e) => setStatus(e.target.value)} fullWidth
            className={classes.textField} InputLabelProps={{ shrink: true }}
            helperText="Badge shown on the card">
            {STATUS_OPTIONS.map((o) => (
              <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <TextField label="Priority" type="number" variant="outlined" size="small"
            value={priority} onChange={(e) => setPriority(e.target.value)} fullWidth
            className={classes.textField} InputLabelProps={{ shrink: true }} />
        </Grid>
        <Grid item xs={6} sm={4} md={3}>
          <IconField label="Icon" value={icon} onChange={setIcon} />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField label="Status label" variant="outlined" size="small" value={statusLabel}
            onChange={(e) => setStatusLabel(e.target.value)} fullWidth
            className={classes.textField} InputLabelProps={{ shrink: true }}
            placeholder="e.g. Live on Web, Android and iOS" />
        </Grid>
        <Grid item xs={12} md={5}>
          <TextField label="Logo image URL (optional)" variant="outlined" size="small" value={logo}
            onChange={(e) => setLogo(e.target.value)} fullWidth
            className={classes.textField} InputLabelProps={{ shrink: true }}
            placeholder="/static/products/Esay.png" />
        </Grid>
        <Grid item xs={12} md={3}>
          <Box className={classes.statusRow}>
            <Switch checked={visible} onChange={() => setVisible(!visible)} color="primary" />
            <span>Visible: <span className={visible ? "on" : "off"}>{visible ? "Yes" : "No"}</span></span>
          </Box>
        </Grid>
        <Grid item xs={4}>
          <TextField label="Accent from" variant="outlined" size="small" value={accentFrom}
            onChange={(e) => setAccentFrom(e.target.value)} fullWidth
            className={classes.textField} InputLabelProps={{ shrink: true }} />
        </Grid>
        <Grid item xs={4}>
          <TextField label="Accent to" variant="outlined" size="small" value={accentTo}
            onChange={(e) => setAccentTo(e.target.value)} fullWidth
            className={classes.textField} InputLabelProps={{ shrink: true }} />
        </Grid>
        <Grid item xs={4}>
          <TextField label="Accent soft" variant="outlined" size="small" value={accentSoft}
            onChange={(e) => setAccentSoft(e.target.value)} fullWidth
            className={classes.textField} InputLabelProps={{ shrink: true }} />
        </Grid>

        {/* Copy */}
        <Grid item xs={12}><Typography className={classes.sectionTitle}>Copy</Typography></Grid>
        <Grid item xs={12} md={6}>
          <TextField label="Expansion (subtitle)" variant="outlined" size="small" value={expansion}
            onChange={(e) => setExpansion(e.target.value)} fullWidth
            className={classes.textField} InputLabelProps={{ shrink: true }} />
        </Grid>
        <Grid item xs={12}>
          <TextField label="Tagline (hero)" variant="outlined" size="small" value={tagline}
            onChange={(e) => setTagline(e.target.value)} fullWidth multiline
            className={classes.textField} InputLabelProps={{ shrink: true }} />
        </Grid>
        <Grid item xs={12}>
          <TextField label="List summary (card)" variant="outlined" size="small" value={listSummary}
            onChange={(e) => setListSummary(e.target.value)} fullWidth multiline
            className={classes.textField} InputLabelProps={{ shrink: true }} />
        </Grid>
        <Grid item xs={12}>
          <TextField label="Overview" variant="outlined" size="small" value={overview}
            onChange={(e) => setOverview(e.target.value)} fullWidth multiline rows={3}
            className={classes.textField} InputLabelProps={{ shrink: true }} />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField label="Features title" variant="outlined" size="small" value={featuresTitle}
            onChange={(e) => setFeaturesTitle(e.target.value)} fullWidth
            className={classes.textField} InputLabelProps={{ shrink: true }} />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField label="Features subtitle" variant="outlined" size="small" value={featuresSubtitle}
            onChange={(e) => setFeaturesSubtitle(e.target.value)} fullWidth
            className={classes.textField} InputLabelProps={{ shrink: true }} />
        </Grid>
        <Grid item xs={12}>
          <TextField label="Founder quote" variant="outlined" size="small" value={quote}
            onChange={(e) => setQuote(e.target.value)} fullWidth multiline
            className={classes.textField} InputLabelProps={{ shrink: true }} />
        </Grid>

        {/* Hero badges */}
        <Grid item xs={12}>
          <RepeaterHead title="Hero badges" onAdd={() => setHeroBadges([...heroBadges, ""])} />
          {heroBadges.map((b, i) => (
            <Box key={i} style={{ display: "flex", gap: 8, marginBottom: 8 }}>
              <TextField variant="outlined" size="small" value={b} fullWidth
                className={classes.textField}
                onChange={(e) => setHeroBadges(heroBadges.map((x, idx) => (idx === i ? e.target.value : x)))} />
              <IconButton size="small" onClick={() => del(setHeroBadges, heroBadges, i)}>
                <DeleteOutlineIcon />
              </IconButton>
            </Box>
          ))}
        </Grid>

        {/* Highlights */}
        <Grid item xs={12}>
          <RepeaterHead title="Highlights (icon + label)" onAdd={() => add(setHighlights, highlights, { icon: "Star", label: "" })} />
          {highlights.map((h, i) => (
            <Grid container spacing={1} key={i} style={{ marginBottom: 6 }}>
              <Grid item xs={4}><IconField label="Icon" value={h.icon} onChange={(v) => upd(highlights, setHighlights, i, "icon", v)} /></Grid>
              <Grid item xs={7}><TextField label="Label" variant="outlined" size="small" value={s(h.label)} fullWidth className={classes.textField} InputLabelProps={{ shrink: true }} onChange={(e) => upd(highlights, setHighlights, i, "label", e.target.value)} /></Grid>
              <Grid item xs={1}><IconButton size="small" onClick={() => del(setHighlights, highlights, i)}><DeleteOutlineIcon /></IconButton></Grid>
            </Grid>
          ))}
        </Grid>

        {/* Features */}
        <Grid item xs={12}>
          <RepeaterHead title="Features (icon + title + desc)" onAdd={() => add(setFeatures, features, { icon: "Star", title: "", desc: "" })} />
          {features.map((f, i) => (
            <Grid container spacing={1} key={i} style={{ marginBottom: 6 }}>
              <Grid item xs={12} sm={3}><IconField label="Icon" value={f.icon} onChange={(v) => upd(features, setFeatures, i, "icon", v)} /></Grid>
              <Grid item xs={12} sm={3}><TextField label="Title" variant="outlined" size="small" value={s(f.title)} fullWidth className={classes.textField} InputLabelProps={{ shrink: true }} onChange={(e) => upd(features, setFeatures, i, "title", e.target.value)} /></Grid>
              <Grid item xs={11} sm={5}><TextField label="Description" variant="outlined" size="small" value={s(f.desc)} fullWidth className={classes.textField} InputLabelProps={{ shrink: true }} onChange={(e) => upd(features, setFeatures, i, "desc", e.target.value)} /></Grid>
              <Grid item xs={1}><IconButton size="small" onClick={() => del(setFeatures, features, i)}><DeleteOutlineIcon /></IconButton></Grid>
            </Grid>
          ))}
        </Grid>

        {/* Categories */}
        <Grid item xs={12}><Typography className={classes.sectionTitle}>Categories section (optional)</Typography></Grid>
        <Grid item xs={12} md={6}><TextField label="Categories title" variant="outlined" size="small" value={catTitle} fullWidth className={classes.textField} InputLabelProps={{ shrink: true }} onChange={(e) => setCatTitle(e.target.value)} /></Grid>
        <Grid item xs={12} md={6}><TextField label="Categories subtitle" variant="outlined" size="small" value={catSubtitle} fullWidth className={classes.textField} InputLabelProps={{ shrink: true }} onChange={(e) => setCatSubtitle(e.target.value)} /></Grid>
        <Grid item xs={12}>
          <RepeaterHead title="Category items" onAdd={() => add(setCatItems, catItems, { icon: "Business", title: "", desc: "" })} />
          {catItems.map((c, i) => (
            <Grid container spacing={1} key={i} style={{ marginBottom: 6 }}>
              <Grid item xs={12} sm={3}><IconField label="Icon" value={c.icon} onChange={(v) => upd(catItems, setCatItems, i, "icon", v)} /></Grid>
              <Grid item xs={12} sm={3}><TextField label="Title" variant="outlined" size="small" value={s(c.title)} fullWidth className={classes.textField} InputLabelProps={{ shrink: true }} onChange={(e) => upd(catItems, setCatItems, i, "title", e.target.value)} /></Grid>
              <Grid item xs={11} sm={5}><TextField label="Description" variant="outlined" size="small" value={s(c.desc)} fullWidth className={classes.textField} InputLabelProps={{ shrink: true }} onChange={(e) => upd(catItems, setCatItems, i, "desc", e.target.value)} /></Grid>
              <Grid item xs={1}><IconButton size="small" onClick={() => del(setCatItems, catItems, i)}><DeleteOutlineIcon /></IconButton></Grid>
            </Grid>
          ))}
        </Grid>

        {/* Awards */}
        <Grid item xs={12}><Typography className={classes.sectionTitle}>Awards section (optional)</Typography></Grid>
        <Grid item xs={12} md={6}><TextField label="Awards title" variant="outlined" size="small" value={awTitle} fullWidth className={classes.textField} InputLabelProps={{ shrink: true }} onChange={(e) => setAwTitle(e.target.value)} /></Grid>
        <Grid item xs={12} md={6}><TextField label="Awards subtitle" variant="outlined" size="small" value={awSubtitle} fullWidth className={classes.textField} InputLabelProps={{ shrink: true }} onChange={(e) => setAwSubtitle(e.target.value)} /></Grid>
        <Grid item xs={12}>
          <RepeaterHead title="Award items" onAdd={() => add(setAwItems, awItems, { icon: "EmojiEvents", title: "", org: "", year: "", place: "" })} />
          {awItems.map((a, i) => (
            <Grid container spacing={1} key={i} style={{ marginBottom: 6 }}>
              <Grid item xs={6} sm={2}><IconField label="Icon" value={a.icon} onChange={(v) => upd(awItems, setAwItems, i, "icon", v)} /></Grid>
              <Grid item xs={6} sm={3}><TextField label="Title" variant="outlined" size="small" value={s(a.title)} fullWidth className={classes.textField} InputLabelProps={{ shrink: true }} onChange={(e) => upd(awItems, setAwItems, i, "title", e.target.value)} /></Grid>
              <Grid item xs={12} sm={3}><TextField label="Org" variant="outlined" size="small" value={s(a.org)} fullWidth className={classes.textField} InputLabelProps={{ shrink: true }} onChange={(e) => upd(awItems, setAwItems, i, "org", e.target.value)} /></Grid>
              <Grid item xs={5} sm={1}><TextField label="Year" variant="outlined" size="small" value={s(a.year)} fullWidth className={classes.textField} InputLabelProps={{ shrink: true }} onChange={(e) => upd(awItems, setAwItems, i, "year", e.target.value)} /></Grid>
              <Grid item xs={6} sm={2}><TextField label="Place" variant="outlined" size="small" value={s(a.place)} fullWidth className={classes.textField} InputLabelProps={{ shrink: true }} onChange={(e) => upd(awItems, setAwItems, i, "place", e.target.value)} /></Grid>
              <Grid item xs={1}><IconButton size="small" onClick={() => del(setAwItems, awItems, i)}><DeleteOutlineIcon /></IconButton></Grid>
            </Grid>
          ))}
        </Grid>

        {/* Platforms */}
        <Grid item xs={12}>
          <RepeaterHead title="Platforms / download links (icon + label + href)" onAdd={() => add(setPlatforms, platforms, { icon: "Language", label: "", href: "" })} />
          {platforms.map((p, i) => (
            <Grid container spacing={1} key={i} style={{ marginBottom: 6 }}>
              <Grid item xs={12} sm={3}><IconField label="Icon" value={p.icon} onChange={(v) => upd(platforms, setPlatforms, i, "icon", v)} /></Grid>
              <Grid item xs={12} sm={3}><TextField label="Label" variant="outlined" size="small" value={s(p.label)} fullWidth className={classes.textField} InputLabelProps={{ shrink: true }} onChange={(e) => upd(platforms, setPlatforms, i, "label", e.target.value)} /></Grid>
              <Grid item xs={11} sm={5}><TextField label="Link" variant="outlined" size="small" value={s(p.href)} fullWidth className={classes.textField} InputLabelProps={{ shrink: true }} onChange={(e) => upd(platforms, setPlatforms, i, "href", e.target.value)} /></Grid>
              <Grid item xs={1}><IconButton size="small" onClick={() => del(setPlatforms, platforms, i)}><DeleteOutlineIcon /></IconButton></Grid>
            </Grid>
          ))}
        </Grid>

        {/* CTA */}
        <Grid item xs={12}><Typography className={classes.sectionTitle}>Call to action</Typography></Grid>
        <Grid item xs={12} md={4}><TextField label="CTA label" variant="outlined" size="small" value={ctaLabel} fullWidth className={classes.textField} InputLabelProps={{ shrink: true }} onChange={(e) => setCtaLabel(e.target.value)} /></Grid>
        <Grid item xs={12} md={4}><TextField label="CTA internal link (to)" variant="outlined" size="small" value={ctaTo} fullWidth className={classes.textField} InputLabelProps={{ shrink: true }} onChange={(e) => setCtaTo(e.target.value)} placeholder="/get-in-touch" /></Grid>
        <Grid item xs={12} md={4}><TextField label="CTA external link (href)" variant="outlined" size="small" value={ctaHref} fullWidth className={classes.textField} InputLabelProps={{ shrink: true }} onChange={(e) => setCtaHref(e.target.value)} placeholder="https://esay.in" /></Grid>
        <Grid item xs={12}><TextField label="CTA note" variant="outlined" size="small" value={ctaNote} fullWidth multiline className={classes.textField} InputLabelProps={{ shrink: true }} onChange={(e) => setCtaNote(e.target.value)} /></Grid>

        {/* SEO */}
        <Grid item xs={12}><Typography className={classes.sectionTitle}>SEO meta</Typography></Grid>
        <Grid item xs={12} md={6}><TextField label="Meta title" variant="outlined" size="small" value={metaTitle} fullWidth className={classes.textField} InputLabelProps={{ shrink: true }} onChange={(e) => setMetaTitle(e.target.value)} /></Grid>
        <Grid item xs={12} md={6}><TextField label="OG image URL" variant="outlined" size="small" value={ogImage} fullWidth className={classes.textField} InputLabelProps={{ shrink: true }} onChange={(e) => setOgImage(e.target.value)} /></Grid>
        <Grid item xs={12} md={6}><TextField label="Meta description" variant="outlined" size="small" value={metaDesc} fullWidth multiline rows={2} className={classes.textField} InputLabelProps={{ shrink: true }} onChange={(e) => setMetaDesc(e.target.value)} /></Grid>
        <Grid item xs={12} md={6}><TextField label="Meta keywords" variant="outlined" size="small" value={metaKeywords} fullWidth multiline rows={2} className={classes.textField} InputLabelProps={{ shrink: true }} onChange={(e) => setMetaKeywords(e.target.value)} /></Grid>

        {/* Advanced */}
        <Grid item xs={12}>
          <Typography className={classes.sectionTitle}>
            Advanced sections (JSON) &mdash; saasModel, toolkit, replaces, howItWorks
          </Typography>
          <TextField variant="outlined" size="small" value={advanced} fullWidth multiline rows={8}
            className={classes.textField} InputLabelProps={{ shrink: true }}
            onChange={(e) => setAdvanced(e.target.value)}
            helperText="Anything not covered by the fields above. Must be valid JSON." />
        </Grid>
      </Grid>

      <Box className={classes.formFooter}>
        <Button className={classes.secondaryBtn} onClick={() => navigate("/dash/allproducts")}>
          Cancel
        </Button>
        <Button onClick={onSubmit} disabled={submitting} className={classes.primaryBtn}
          endIcon={!submitting && <ArrowForwardIcon />}>
          {submitting ? "Saving..." : mode === "edit" ? "Save Changes" : "Add Product"}
        </Button>
      </Box>
    </div>
  );
};

export const AddProduct = () => <ProductForm mode="add" />;
